import React, { useEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import {
  Bold, Italic, Underline as UnderlineIcon, Heading2, Heading3, List, ListOrdered,
  Quote, Link2, Unlink, ImagePlus, Undo2, Redo2, Minus, Loader2,
} from "lucide-react";
import { uploadImage, imgUrl, isHtmlContent } from "../lib/api";

// Converts legacy plain-text (blank-line paragraphs) to HTML for the editor
const toHtml = (s) => {
  if (!s) return "";
  if (isHtmlContent(s)) return s;
  return s.split(/\n\s*\n/).filter(Boolean).map((p) => `<p>${p.replace(/\n/g, "<br>")}</p>`).join("");
};

const Btn = ({ onClick, active, title, children, disabled, testId }) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    disabled={disabled}
    title={title}
    data-testid={testId}
    className={`p-2 rounded-md transition-colors disabled:opacity-30 ${
      active ? "bg-navy-900 text-amber-400" : "text-slate-600 hover:bg-slate-200 hover:text-navy-900"
    }`}
  >
    {children}
  </button>
);

const RichEditor = ({ value, onChange, testId = "rich-editor" }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        link: { openOnClick: false, autolink: true, HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" } },
      }),
      Image.configure({ inline: false, HTMLAttributes: { class: "rounded-xl" } }),
    ],
    content: toHtml(value),
    editorProps: { attributes: { class: "rich-content min-h-[260px] px-4 py-3 outline-none", "data-testid": `${testId}-content` } },
    onUpdate: ({ editor: ed }) => onChange(ed.isEmpty ? "" : ed.getHTML()),
  });

  // Sync external value change (e.g. switching post) without loop
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = toHtml(value);
    if (next !== current && !(editor.isEmpty && !value)) editor.commands.setContent(next, { emitUpdate: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, value === "" ? "" : undefined]);

  if (!editor) return null;

  const setLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Bağlantı URL'si (https://...)", prev);
    if (url === null) return;
    if (url === "") return editor.chain().focus().extendMarkRange("link").unsetLink().run();
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const addImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      editor.chain().focus().setImage({ src: url, alt: file.name }).run();
    } catch (e) {
      alert(e?.response?.data?.detail || "Görsel yüklenemedi");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-300 bg-white focus-within:border-amber-500 focus-within:ring-2 focus-within:ring-amber-500/20 transition-colors" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-0.5 border-b border-slate-200 bg-slate-50 px-2 py-1.5 rounded-t-xl">
        <Btn title="Başlık 2" testId={`${testId}-h2`} active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 className="w-4 h-4" /></Btn>
        <Btn title="Başlık 3" testId={`${testId}-h3`} active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><Heading3 className="w-4 h-4" /></Btn>
        <span className="w-px h-5 bg-slate-300 mx-1" />
        <Btn title="Kalın" testId={`${testId}-bold`} active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><Bold className="w-4 h-4" /></Btn>
        <Btn title="İtalik" testId={`${testId}-italic`} active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic className="w-4 h-4" /></Btn>
        <Btn title="Altı çizili" testId={`${testId}-underline`} active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()}><UnderlineIcon className="w-4 h-4" /></Btn>
        <span className="w-px h-5 bg-slate-300 mx-1" />
        <Btn title="Madde listesi" testId={`${testId}-bullet`} active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><List className="w-4 h-4" /></Btn>
        <Btn title="Numaralı liste" testId={`${testId}-ordered`} active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered className="w-4 h-4" /></Btn>
        <Btn title="Alıntı" testId={`${testId}-quote`} active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()}><Quote className="w-4 h-4" /></Btn>
        <Btn title="Ayraç" testId={`${testId}-hr`} onClick={() => editor.chain().focus().setHorizontalRule().run()}><Minus className="w-4 h-4" /></Btn>
        <span className="w-px h-5 bg-slate-300 mx-1" />
        <Btn title="Bağlantı" testId={`${testId}-link`} active={editor.isActive("link")} onClick={setLink}><Link2 className="w-4 h-4" /></Btn>
        <Btn title="Bağlantıyı kaldır" testId={`${testId}-unlink`} disabled={!editor.isActive("link")} onClick={() => editor.chain().focus().unsetLink().run()}><Unlink className="w-4 h-4" /></Btn>
        <Btn title="Görsel ekle" testId={`${testId}-image`} disabled={uploading} onClick={() => fileRef.current?.click()}>
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
        </Btn>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid={`${testId}-image-input`} onChange={(e) => { addImage(e.target.files?.[0]); e.target.value = ""; }} />
        <span className="flex-1" />
        <Btn title="Geri al" testId={`${testId}-undo`} disabled={!editor.can().undo()} onClick={() => editor.chain().focus().undo().run()}><Undo2 className="w-4 h-4" /></Btn>
        <Btn title="Yinele" testId={`${testId}-redo`} disabled={!editor.can().redo()} onClick={() => editor.chain().focus().redo().run()}><Redo2 className="w-4 h-4" /></Btn>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
};

export { imgUrl };
export default RichEditor;
